import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ConfirmDialog from './ConfirmDialog';
import MonitoringCtoModal from '../modals/MonitoringCtoModal';
import MonitoringServiceOrderModal from '../modals/MonitoringServiceOrderModal';
import { updateMonitoringClient } from '../../services/apiService';
import html2canvas from 'html2canvas';
import { Camera, Copy, ExternalLink, MapPin, Move, Palette, Search, Link } from 'lucide-react';

const menuItemClass = 'flex items-center gap-2 w-full text-left px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-700 rounded';

export default function MonitoringMapActions({ mapRef, menu, setMenu, theme, onUpdated, onLocateBox, onToggleMapColor, isAdmin, canOpenServiceOrder = false }) {
    const [serviceOrderBox, setServiceOrderBox] = useState(null);
    const [orders, setOrders] = useState({});
    const [moving, setMoving] = useState(null);
    const [confirmation, setConfirmation] = useState(null);
    const [ctoClient, setCtoClient] = useState(null);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const menuRef = useRef(null);
    const closeConfirmation = useCallback(() => setConfirmation(null), []);
    const closeCto = useCallback(() => setCtoClient(null), []);
    useEffect(() => {
        const map = mapRef.current;
        const close = () => setMenu(null);
        const keydown = event => { if (event.key === 'Escape') { setMenu(null); setMoving(null); } };
        map.on('click zoomstart movestart', close);
        window.addEventListener('keydown', keydown);
        return () => { map.off('click zoomstart movestart', close); window.removeEventListener('keydown', keydown); };
    }, [mapRef, setMenu]);
    useEffect(() => {
        if (!menu) return undefined;
        const outside = event => {
            if (menuRef.current?.contains(event.target)) return;
            // Blank map clicks are handled by Leaflet, which must dismiss rather than reopen.
            if (mapRef.current.getContainer().contains(event.target) && !event.target.closest?.('.leaflet-control')) return;
            setMenu(null);
        };
        document.addEventListener('pointerdown', outside);
        return () => document.removeEventListener('pointerdown', outside);
    }, [menu, mapRef, setMenu]);
    useEffect(() => {
        const map = mapRef.current;
        const open = event => {
            if (busy || moving || confirmation || ctoClient || serviceOrderBox || !event.latlng) return;
            if (event.propagatedFrom || (event.sourceTarget && event.sourceTarget !== map)) return;
            if (event.originalEvent?.target?.closest?.('.leaflet-interactive, .leaflet-marker-icon, .leaflet-control, .leaflet-popup')) return;
            event.originalEvent?.preventDefault();
            const point = event.containerPoint || map.latLngToContainerPoint(event.latlng);
            const container = map.getContainer();
            setMenu({ coordinates: { lat: event.latlng.lat, lng: event.latlng.lng },
                x: Math.max(0, Math.min(point.x, container.clientWidth - 224)),
                y: Math.max(0, Math.min(point.y, container.clientHeight - 210)) });
        };
        map.on('contextmenu', open);
        return () => map.off('contextmenu', open);
    }, [mapRef, setMenu, busy, moving, confirmation, ctoClient, serviceOrderBox]);
    useEffect(() => {
        const map = mapRef.current;
        if (!moving) return;
        const container = map.getContainer();
        container.classList.add('monitoring-moving');
        const click = event => {
            setConfirmation({ client: moving, action: 'move', coordinates: { lat: event.latlng.lat, lng: event.latlng.lng } });
            setMoving(null);
        };
        map.on('click', click);
        return () => { container.classList.remove('monitoring-moving'); map.off('click', click); };
    }, [moving, mapRef]);
    const updated = (client, result) => { onUpdated(client, result); setMessage(result.warning || 'Alteração salva no IXC.'); };
    const execute = async () => {
        if (busy) return;
        setBusy(true); setMessage(''); setErrorMessage('');
        try {
            const result = await updateMonitoringClient({ clientId: confirmation.client.id, action: confirmation.action, coordinates: confirmation.coordinates, confirmed: true });
            updated(confirmation.client, result);
            setConfirmation(null);
        } catch (error) { setErrorMessage(error.response?.data?.message || error.message || 'Não foi possível salvar a localização no IXC.'); setConfirmation(null); }
        finally { setBusy(false); }
    };
    const copyCoordinates = async () => {
        const { lat, lng } = menu.coordinates;
        setMenu(null);
        try {
            await navigator.clipboard.writeText(`${lat}, ${lng}`);
            setMessage('Coordenadas copiadas!');
        } catch { setMessage('Não foi possível copiar as coordenadas. Verifique a permissão da área de transferência.'); }
    };
    const openMaps = streetView => {
        const { lat, lng } = menu.coordinates;
        window.open(streetView ? `https://www.google.com/maps?layer=c&cbll=${lat},${lng}` : `https://www.google.com/maps?q=${lat},${lng}`, '_blank', 'noopener,noreferrer');
        setMenu(null);
    };
    const screenshot = async () => {
        setMenu(null); setMessage(''); setBusy(true);
        try {
            const canvas = await html2canvas(mapRef.current.getContainer(), { useCORS: true, logging: false });
            const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
            if (!blob) throw new Error('Não foi possível gerar a imagem do mapa.');
            let copied = false;
            if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
                try {
                    await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
                    copied = true;
                } catch { /* Download when image clipboard is unavailable or denied. */ }
            }
            if (copied) setMessage('Screenshot copiado!');
            else {
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url; link.download = 'monitoramento-mapa.png'; link.click();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
                setMessage('Screenshot baixado!');
            }
        } catch (error) { setMessage(error.message || 'Não foi possível obter o screenshot.'); }
        finally { setBusy(false); }
    };
    return <>
        {menu && (!menu.client || isAdmin) && (!menu.box || canOpenServiceOrder) && !busy && !moving && !confirmation && !ctoClient && !serviceOrderBox && <div ref={menuRef} role="menu" aria-label={menu.box ? 'Ações da CTO' : menu.client ? 'Ações do cliente' : 'Ações do mapa'} className="absolute z-20 w-56 p-1 rounded-lg shadow-xl border bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100" style={{ left: menu.x, top: menu.y }} onClick={event => event.stopPropagation()} onContextMenu={event => event.preventDefault()}>
            {menu.box ? <>
                {orders[menu.box.id] ? <p role="status" className="p-3 text-sm">O.S. {orders[menu.box.id].orderId} · {orders[menu.box.id].protocol}</p> :
                    <button type="button" role="menuitem" className={menuItemClass} onClick={() => { setServiceOrderBox(menu.box); setMenu(null); }}>Abrir O.S.</button>}
            </> : menu.client ? <>
                <button type="button" role="menuitem" className={menuItemClass} onClick={() => { setMoving(menu.client); setMenu(null); setMessage(''); }}><Move size={16} />Mover</button>
                <button type="button" role="menuitem" className={menuItemClass} onClick={() => { setConfirmation({ client: menu.client, action: 'geocode' }); setMenu(null); }}><Search size={16} />Buscar localização</button>
                <hr className="my-1" /><button type="button" role="menuitem" className={menuItemClass} onClick={() => { setCtoClient(menu.client); setMenu(null); }}><Link size={16} />{menu.client.ctoId ? 'Alterar vínculo' : 'Vincular CTO'}</button>
            </> : <>
                <button type="button" role="menuitem" className={menuItemClass} onClick={copyCoordinates}><Copy size={16} className="text-blue-500" />Copiar coordenadas</button>
                <button type="button" role="menuitem" className={menuItemClass} onClick={() => openMaps(false)}><MapPin size={16} className="text-red-500" />Abrir no Maps</button>
                <button type="button" role="menuitem" className={menuItemClass} onClick={() => openMaps(true)}><ExternalLink size={16} className="text-yellow-500" />Abrir no Street View</button>
                <hr className="my-1 border-slate-200 dark:border-slate-700" />
                <button type="button" role="menuitem" className={menuItemClass} onClick={screenshot}><Camera size={16} className="text-purple-500" />Obter Screenshot</button>
                <button type="button" role="menuitem" className={menuItemClass} onClick={() => { onToggleMapColor(); setMenu(null); }}><Palette size={16} className="text-green-500" />Alterar modo de cor</button>
            </>}
        </div>}
        {moving && <div role="status" className="absolute top-2 left-2 z-20 rounded-lg bg-white dark:bg-slate-800 p-3 shadow">Clique no novo local de {moving.name || moving.id}. <button type="button" className="underline" onClick={() => setMoving(null)}>Cancelar</button></div>}
        {message && <p role="status" className="mt-2 text-sm">{message}</p>}
        {errorMessage && <div role="alert" className="absolute top-2 left-2 right-2 z-30 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900 shadow-lg dark:border-red-700 dark:bg-red-950 dark:text-red-100">
            <div className="flex items-start justify-between gap-3">
                <div><p className="font-semibold">Não foi possível salvar a localização</p><p className="mt-1 max-h-40 overflow-auto break-words">{errorMessage}</p></div>
                <button type="button" className="shrink-0 underline" onClick={() => setErrorMessage('')} aria-label="Fechar erro de localização">Fechar</button>
            </div>
        </div>}
        {busy && <p role="status">Processando…</p>}
        {ctoClient && <MonitoringCtoModal client={ctoClient} theme={theme} onClose={closeCto} onUpdated={updated} onLocateBox={onLocateBox} />}
        {serviceOrderBox && <MonitoringServiceOrderModal box={serviceOrderBox} theme={theme} onClose={() => setServiceOrderBox(null)} onCreated={result => setOrders(previous => ({ ...previous, [serviceOrderBox.id]: result }))} />}
        {confirmation && createPortal(<div className={theme === 'dark' ? 'dark' : ''} style={{ position: 'relative', zIndex: 2200 }}><ConfirmDialog isOpen title="Confirmar localização no IXC" message={`${confirmation.action === 'move' ? `Mover ${confirmation.client.name || confirmation.client.id} para ${confirmation.coordinates.lat.toFixed(6)}, ${confirmation.coordinates.lng.toFixed(6)} e atualizar o IXC?` : `Buscar a localização pelo endereço de ${confirmation.client.name || confirmation.client.id} e atualizar as coordenadas no IXC?`} A posição ficará específica desta instalação, mantendo seu endereço.`} isProcessing={busy} onClose={closeConfirmation} onConfirm={execute} /></div>, document.fullscreenElement || document.body)}
    </>;
}
